/**
 * Drawer — le tiroir : une fenêtre ancrée à droite (lot 3 du catalogue, 28-10-2026 : retapé).
 *
 * Pourquoi distinct de la Modale : elle se centre ; lui se colle à droite et laisse la liste
 * visible à gauche (maître-détail : un membre, un fan, un média ouvert depuis une liste). Même
 * mécanique (ouverture.ts) et même dessin (ouverture.css) : au-delà de 640 px il FLOTTE, à
 * 12 px des bords, tout en hauteur, arrondi des quatre côtés, et entre de la droite ; sur
 * mobile il est la feuille collée en bas (90 % de la hauteur au plus), comme la Modale.
 *
 *   <Drawer isOpen={open} onClose={close} title="Membre">
 *     <DrawerBody>…</DrawerBody>
 *     <DrawerFooter>…</DrawerFooter>
 *   </Drawer>
 *
 * Tailles : sm ~24 rem · md ~28 rem (défaut, le détail d'un membre) · lg ~32 rem · xl ~42 rem
 * (une galerie, un gros formulaire). Sur mobile, toute la largeur quelle que soit la taille.
 *
 * `DrawerHeader` / `DrawerBody` / `DrawerFooter` SONT ceux de la Modale (un seul dessin).
 */
import { useCallback, useRef, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { ModalBody, ModalFooter, ModalHeader } from "./Modal";
import {
  Z_INDEX_CLASS,
  piegerLeTab,
  useCadreDuMarqueur,
  useFermerAEchap,
  useFocusDansLaFenetre,
  useVerrouDuDefilement,
  type ZIndexOuverture,
} from "./ouverture";
import { cn } from "./utils";
import "./ouverture.css";

export type DrawerSize = "sm" | "md" | "lg" | "xl";

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  size?: DrawerSize;
  /** Un titre : l'en-tête standard, avec la croix. */
  title?: string;
  subtitle?: string;
  ariaLabel?: string;
  /** Défaut 400 (au-dessus du shell mobile du cockpit). */
  zIndex?: ZIndexOuverture;
  dismissOnBackdropClick?: boolean;
  dismissOnEscape?: boolean;
  noBodyLock?: boolean;
  children: ReactNode;
  /** Sur le tiroir. */
  className?: string;
  /** L'étiquette (lecteur d'écran) de la croix. Défaut « Fermer ». */
  closeLabel?: string;
  /** Posé dans la page : ni fond, ni portail, ni verrou, ni focus — le catalogue, un test de rendu. */
  statique?: boolean;
}

const SIZE_TO_WIDTH: Record<DrawerSize, string> = {
  sm: "w-full sm:max-w-sm",
  md: "w-full sm:max-w-md",
  lg: "w-full sm:max-w-lg",
  xl: "w-full sm:max-w-2xl",
};

export function Drawer({
  isOpen,
  onClose,
  size = "md",
  title,
  subtitle,
  ariaLabel,
  zIndex = 400,
  dismissOnBackdropClick = true,
  dismissOnEscape = true,
  noBodyLock = false,
  children,
  className,
  closeLabel,
  statique = false,
}: DrawerProps) {
  const fenetre = useRef<HTMLDivElement>(null);
  const marqueur = useRef<HTMLSpanElement>(null);
  const vivant = isOpen && !statique;

  useFermerAEchap(vivant && dismissOnEscape, onClose);
  useVerrouDuDefilement(vivant && !noBodyLock);
  useFocusDansLaFenetre(vivant, fenetre);
  const cadre = useCadreDuMarqueur(vivant, marqueur);

  const handleBackdrop = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!dismissOnBackdropClick) return;
      if (e.target === e.currentTarget) onClose();
    },
    [dismissOnBackdropClick, onClose],
  );

  if (!isOpen) return <span hidden ref={marqueur} />;
  if (typeof document === "undefined") return null;

  const contenu = (
    <>
      {title && <DrawerHeader title={title} subtitle={subtitle} onClose={onClose} closeLabel={closeLabel} />}
      {children}
    </>
  );

  if (statique) {
    return (
      <div role="dialog" aria-label={ariaLabel ?? title ?? undefined} className={cn("ov-fenetre ov-arrondi cockpit-modal", SIZE_TO_WIDTH[size], className)}>
        {contenu}
      </div>
    );
  }

  // `items-end` : la feuille du bas sur mobile ; `items-stretch justify-end` : tout en hauteur,
  // collé à droite au-delà (la marge de 12 px vient de `data-mise="tiroir"`, ouverture.css).
  // Le marqueur reste à sa place dans l'arbre : c'est là que se lit le thème recopié.
  return (
    <>
      <span hidden ref={marqueur} />
      {createPortal(
    <div className={cadre.sombre ? "dark" : undefined} data-theme={cadre.theme}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title ?? undefined}
        onClick={handleBackdrop}
        data-mise="tiroir"
        className={cn("ov-fond cockpit-modal-fond fixed inset-0 flex items-end justify-end sm:items-stretch", Z_INDEX_CLASS[zIndex])}
      >
        <div
          ref={fenetre}
          tabIndex={-1}
          onKeyDown={piegerLeTab}
          onClick={(e) => e.stopPropagation()}
          data-entree="tiroir"
          className={cn(
            "ov-fenetre ov-arrondi-feuille cockpit-modal max-h-[calc(90dvh/var(--zoom-affichage,1))] sm:max-h-none",
            SIZE_TO_WIDTH[size],
            className,
          )}
        >
          {contenu}
        </div>
      </div>
    </div>,
        document.body,
      )}
    </>
  );
}

export const DrawerHeader = ModalHeader;
export const DrawerBody = ModalBody;
export const DrawerFooter = ModalFooter;
